import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';

export enum QuestionType {
  MultipleChoice = 1,
  TrueFalse = 2
}

export interface QuizDto {
  id: string;
  title: string;
  description?: string;
  category?: string;
  minimumScore: number;
  timeLimitMinutes?: number;
  questions: QuestionDto[];
  questionsCount: number;
  rules: QuizRuleDto[];
  createdBy: string;
  creatorName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateQuizDto {
  title: string;
  description?: string;
  category?: string;
  minimumScore: number;
  timeLimitMinutes?: number;
  questions: CreateQuestionDto[];
  rules: CreateQuizRuleDto[];
  trainingTeamIds?: string[];
}

export interface UpdateQuizDto {
  title: string;
  description?: string;
  category?: string;
  minimumScore: number;
  timeLimitMinutes?: number;
  isActive: boolean;
  trainingTeamIds?: string[];
}

export interface QuizListDto {
  id: string;
  title: string;
  category?: string;
  minimumScore: number;
  questionsCount: number;
  creatorName: string;
  isActive: boolean;
  createdAt: string;
}

export interface QuestionDto {
  id: string;
  text: string;
  type: QuestionType;
  points: number;
  order: number;
  options: QuestionOptionDto[];
  correctAnswerId?: string;
}

export interface CreateQuestionDto {
  text: string;
  type: QuestionType;
  points: number;
  order: number;
  options: CreateQuestionOptionDto[];
}

export interface UpdateQuestionDto {
  text: string;
  type: QuestionType;
  points: number;
  order: number;
  options: CreateQuestionOptionDto[];
}

export interface QuestionOptionDto {
  id: string;
  text: string;
  isCorrect: boolean;
}

export interface CreateQuestionOptionDto {
  text: string;
  isCorrect: boolean;
}

export interface QuizRuleDto {
  id: string;
  ruleType: number;
  value: string;
}

export interface CreateQuizRuleDto {
  ruleType: number;
  value: string;
}

export class QuizService extends BaseApiService {
  async getQuizzes(): Promise<QuizListDto[]> {
    const response = await this.api.get<ApiResponse<QuizListDto[]>>('/quizzes');
    return this.extractData(response);
  }

  async getQuiz(id: string): Promise<QuizDto> {
    const response = await this.api.get<ApiResponse<QuizDto>>(`/quizzes/${id}`);
    return this.extractData(response);
  }

  async createQuiz(data: CreateQuizDto): Promise<QuizDto> {
    const response = await this.api.post<ApiResponse<QuizDto>>('/quizzes', data);
    return this.extractData(response);
  }

  async updateQuiz(id: string, data: UpdateQuizDto): Promise<QuizDto> {
    const response = await this.api.put<ApiResponse<QuizDto>>(`/quizzes/${id}`, data);
    return this.extractData(response);
  }

  async deleteQuiz(id: string): Promise<void> {
    await this.api.delete(`/quizzes/${id}`);
  }

  async addQuestion(quizId: string, data: CreateQuestionDto): Promise<QuestionDto> {
    const response = await this.api.post<ApiResponse<QuestionDto>>(`/quizzes/${quizId}/questions`, data);
    return this.extractData(response);
  }

  async updateQuestion(quizId: string, questionId: string, data: UpdateQuestionDto): Promise<QuestionDto> {
    const response = await this.api.put<ApiResponse<QuestionDto>>(`/quizzes/${quizId}/questions/${questionId}`, data);
    return this.extractData(response);
  }

  async deleteQuestion(quizId: string, questionId: string): Promise<void> {
    await this.api.delete(`/quizzes/${quizId}/questions/${questionId}`);
  }

  async addRule(quizId: string, data: CreateQuizRuleDto): Promise<QuizRuleDto> {
    const response = await this.api.post<ApiResponse<QuizRuleDto>>(`/quizzes/${quizId}/rules`, data);
    return this.extractData(response);
  }

  async deleteRule(quizId: string, ruleId: string): Promise<void> {
    await this.api.delete(`/quizzes/${quizId}/rules/${ruleId}`);
  }
}

export const quizService = new QuizService();
export default quizService;

